import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Loader2, X, Search, Trash2, Pencil, Repeat, Receipt } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import EmptyState from "../components/EmptyState";
import dict from "../i18n/es-MX";

const fmtMXN = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);
import { fmtDate } from "../lib/dateFormat";

export default function Gastos() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ category: "", from: "", to: "", recurring: "", q: "" });
    const [editing, setEditing] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [confirmDel, setConfirmDel] = useState(null);
    const [err, setErr] = useState("");

    const load = useCallback(async () => {
        setLoading(true); setErr("");
        const params = {};
        Object.entries(filters).forEach(([k, v]) => { if (v !== "") params[k === "from" ? "from" : k] = v; });
        try {
            const r = await api.get("/general-expenses", { params });
            setRows(r.data);
        } catch (e) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    }, [filters]);
    useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

    const totalMonth = useMemo(() => {
        const now = new Date();
        const ym = now.toISOString().slice(0, 7);
        return rows.filter(r => r.date.startsWith(ym)).reduce((s, r) => s + r.amount_mxn, 0);
    }, [rows]);
    const totalYear = useMemo(() => {
        const y = String(new Date().getFullYear());
        return rows.filter(r => r.date.startsWith(y)).reduce((s, r) => s + r.amount_mxn, 0);
    }, [rows]);

    return (
        <div className="space-y-6" data-testid="gastos-page">
            <header className="flex items-baseline justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">{dict.expenses.title}</h1>
                    <p className="mt-2 text-slate-400 text-sm">{dict.expenses.subtitle}</p>
                </div>
                <button type="button" onClick={() => { setEditing(null); setShowForm(true); }} data-testid="new-expense-btn"
                    className="h-10 px-4 rounded-lg bg-brand-orange hover:bg-brand-orange/90 text-white inline-flex items-center gap-2 text-sm font-semibold">
                    <Plus size={16}/>{dict.expenses.newExpense}
                </button>
            </header>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5" data-testid="kpi-month-total">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">{dict.expenses.totalMonth}</div>
                    <div className="font-display text-3xl font-extrabold text-rose-300 mt-2">{fmtMXN(totalMonth)}</div>
                </div>
                <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5" data-testid="kpi-year-total">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">{dict.expenses.totalYear}</div>
                    <div className="font-display text-3xl font-extrabold text-rose-300 mt-2">{fmtMXN(totalYear)}</div>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 p-3 rounded-xl bg-slate-900/40 border border-slate-800">
                <Search size={14} className="text-slate-500 ml-2"/>
                <input value={filters.q} onChange={e => setFilters({ ...filters, q: e.target.value })} data-testid="filter-q"
                    placeholder={dict.expenses.searchPh}
                    className="h-9 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm min-w-[180px]"/>
                <select value={filters.category} onChange={e => setFilters({ ...filters, category: e.target.value })} data-testid="filter-category"
                    className="h-9 px-2 rounded-lg bg-slate-950 border border-slate-700 text-sm">
                    <option value="">{dict.expenses.allCategories}</option>
                    {Object.entries(dict.expenses.categories).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <select value={filters.recurring} onChange={e => setFilters({ ...filters, recurring: e.target.value })} data-testid="filter-recurring"
                    className="h-9 px-2 rounded-lg bg-slate-950 border border-slate-700 text-sm">
                    <option value="">{dict.expenses.recurringAll}</option>
                    <option value="true">{dict.expenses.recurringYes}</option>
                    <option value="false">{dict.expenses.recurringNo}</option>
                </select>
                <input type="date" value={filters.from} onChange={e => setFilters({ ...filters, from: e.target.value })} data-testid="filter-from"
                    className="h-9 px-2 rounded-lg bg-slate-950 border border-slate-700 text-sm"/>
                <input type="date" value={filters.to} onChange={e => setFilters({ ...filters, to: e.target.value })} data-testid="filter-to"
                    className="h-9 px-2 rounded-lg bg-slate-950 border border-slate-700 text-sm"/>
            </div>

            {err && <div className="text-sm text-red-300">{err}</div>}
            {loading ? <div className="text-center py-10"><Loader2 className="animate-spin inline text-slate-500"/></div> : (
                <div className="rounded-2xl border border-slate-800 overflow-x-auto" data-testid="gastos-table">
                    <table className="min-w-full text-sm">
                        <thead className="bg-slate-900/80 text-xs uppercase text-slate-400">
                            <tr>
                                <th className="text-left p-3">{dict.expenses.cols.date}</th>
                                <th className="text-left p-3">{dict.expenses.cols.category}</th>
                                <th className="text-left p-3">{dict.expenses.cols.description}</th>
                                <th className="text-right p-3">{dict.expenses.cols.amount}</th>
                                <th className="text-center p-3">{dict.expenses.cols.recurring}</th>
                                <th className="text-right p-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.id} className="border-t border-slate-800" data-testid={`gasto-row-${r.id}`}>
                                    <td className="p-3 text-slate-400">{fmtDate(r.date)}</td>
                                    <td className="p-3"><span className="px-2 h-5 text-[10px] rounded-full bg-slate-800 border border-slate-700 inline-flex items-center uppercase tracking-wider">{dict.expenses.categories[r.category] || r.category}</span></td>
                                    <td className="p-3">{r.description}</td>
                                    <td className="p-3 text-right font-mono">{fmtMXN(r.amount_mxn)}</td>
                                    <td className="p-3 text-center">{r.recurring ? <span className="inline-flex items-center gap-1 text-xs text-brand-orange"><Repeat size={12}/>{dict.expenses.periods[r.recurring_period] || "—"}</span> : "—"}</td>
                                    <td className="p-3 text-right space-x-1">
                                        <button type="button" onClick={() => { setEditing(r); setShowForm(true); }} data-testid={`edit-${r.id}`} className="h-8 w-8 inline-flex items-center justify-center rounded border border-slate-700 hover:bg-slate-800"><Pencil size={12}/></button>
                                        {isAdmin && <button type="button" onClick={() => setConfirmDel(r)} data-testid={`del-${r.id}`} className="h-8 w-8 inline-flex items-center justify-center rounded border border-red-500/40 text-red-300 hover:bg-red-500/10"><Trash2 size={12}/></button>}
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && (
                                <tr><td colSpan={6} className="px-0 py-0"><EmptyState icon={Receipt} title={dict.expenses.empty} ctaLabel={dict.expenses.newExpense || "Nuevo gasto"} onCta={() => { setEditing(null); setShowForm(true); }} testid="gastos-empty"/></td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {showForm && <ExpenseForm initial={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }}/>}
            {confirmDel && (
                <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="confirm-del-modal">
                    <div className="rounded-2xl border border-slate-800 bg-slate-900 w-full max-w-md p-6">
                        <h2 className="font-display text-2xl font-bold">{dict.expenses.confirmDel}</h2>
                        <p className="text-slate-400 text-sm mt-2">{confirmDel.description}</p>
                        <div className="flex gap-2 justify-end mt-5">
                            <button type="button" onClick={() => setConfirmDel(null)} className="h-10 px-4 rounded-lg border border-slate-700 hover:bg-slate-800 text-sm">{dict.common.cancel}</button>
                            <button type="button" data-testid="confirm-del-btn" onClick={async () => { await api.delete(`/general-expenses/${confirmDel.id}`); setConfirmDel(null); load(); }} className="h-10 px-4 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-semibold">{dict.common.confirm}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function ExpenseForm({ initial, onClose, onSaved }) {
    const [form, setForm] = useState({
        date: initial?.date || new Date().toISOString().slice(0, 10),
        category: initial?.category || "oficina",
        description: initial?.description || "",
        amount_mxn: initial?.amount_mxn || "",
        recurring: initial?.recurring || false,
        recurring_period: initial?.recurring_period || "mensual",
        notes: initial?.notes || "",
    });
    const [saving, setSaving] = useState(false); const [err, setErr] = useState("");
    const submit = async (e) => {
        e.preventDefault();
        setSaving(true); setErr("");
        try {
            const body = {
                ...form,
                amount_mxn: Number(form.amount_mxn),
                recurring_period: form.recurring ? form.recurring_period : null,
            };
            if (initial?.id) await api.patch(`/general-expenses/${initial.id}`, body);
            else await api.post("/general-expenses", body);
            onSaved();
        } catch (e2) { setErr(formatApiError(e2)); }
        finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="expense-form-modal">
            <form noValidate onSubmit={submit} className="rounded-2xl border border-slate-800 bg-slate-900 w-full max-w-lg p-6 space-y-3">
                <div className="flex items-center justify-between">
                    <h2 className="font-display text-2xl font-bold">{initial ? dict.expenses.editExpense : dict.expenses.newExpense}</h2>
                    <button type="button" onClick={onClose} data-testid="form-close"><X size={20} className="text-slate-500 hover:text-slate-100"/></button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Field label={dict.expenses.cols.date}><input type="date" required value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="form-date" className="w-full h-10 px-3 rounded-lg bg-slate-950 border border-slate-700"/></Field>
                    <Field label={dict.expenses.cols.category}>
                        <select required value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} data-testid="form-category" className="w-full h-10 px-3 rounded-lg bg-slate-950 border border-slate-700">
                            {Object.entries(dict.expenses.categories).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                    </Field>
                </div>
                <Field label={dict.expenses.cols.description}>
                    <input type="text" required value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="form-description" className="w-full h-10 px-3 rounded-lg bg-slate-950 border border-slate-700"/>
                </Field>
                <Field label={dict.expenses.cols.amount}>
                    <input type="number" required step="0.01" min="0.01" value={form.amount_mxn} onChange={e => setForm({ ...form, amount_mxn: e.target.value })} data-testid="form-amount" className="w-full h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 font-mono"/>
                </Field>
                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.recurring} onChange={e => setForm({ ...form, recurring: e.target.checked })} data-testid="form-recurring"/>
                    {dict.expenses.isRecurring}
                </label>
                {form.recurring && (
                    <Field label={dict.expenses.recurringPeriod}>
                        <select value={form.recurring_period} onChange={e => setForm({ ...form, recurring_period: e.target.value })} data-testid="form-period" className="w-full h-10 px-3 rounded-lg bg-slate-950 border border-slate-700">
                            <option value="mensual">{dict.expenses.periods.mensual}</option>
                            <option value="trimestral">{dict.expenses.periods.trimestral}</option>
                            <option value="anual">{dict.expenses.periods.anual}</option>
                        </select>
                    </Field>
                )}
                <Field label={dict.expenses.notesLabel}>
                    <textarea value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="form-notes" className="w-full h-20 px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-sm"/>
                </Field>
                {err && <div className="text-sm text-red-300" data-testid="form-error">{err}</div>}
                <div className="flex gap-2 justify-end">
                    <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-700 text-sm">{dict.common.cancel}</button>
                    <button type="submit" disabled={saving} data-testid="form-submit" className="h-10 px-4 rounded-lg bg-brand-orange text-white text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-2">
                        {saving && <Loader2 size={14} className="animate-spin"/>}{initial ? dict.common.save : dict.common.create}
                    </button>
                </div>
            </form>
        </div>
    );
}

function Field({ label, children }) {
    return <label className="block"><div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{label}</div>{children}</label>;
}
